/**
 * The detail screen of a SEALED company (119-07): the public record and, when
 * the investor had it in their pipeline, their OWN facts about the deal (stage,
 * notes — investor-owned, owner decision #8), read-only.
 *
 * No data room, no stage actions, no messaging entry: a closed / acquired /
 * bankrupt company's data is sealed and nothing about it can be changed. Used by
 * startup-detail in place of the empty "Unknown" screen a NULL profile embed
 * used to produce.
 */

import React from 'react';
import { View, Text } from 'react-native';
import { AppLayout } from '@/components/AppLayout';
import { ScreenFrame } from '@/components/ScreenFrame';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { ClosedCompanyCard } from '@/components/company/ClosedCompanyCard';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import type { CompanyPublicCard } from '@/lib/company/public-card';
import type { DealStage } from '@/lib/dealflow/stage';

const STAGE_LABEL: Record<DealStage, string> = {
  screening: 'Screening',
  interested: 'Interested',
  awaiting_access: 'Awaiting access',
  diligence: 'Due diligence',
  invested: 'Invested',
  rejected: 'Rejected',
  monitoring: 'Monitoring',
};

const DIMENSION_LABEL: Record<string, string> = {
  team: 'Team',
  product: 'Product',
  market: 'Market',
  defensibility: 'Defensibility',
  competition: 'Competition',
  'business-model': 'Business model',
  'vision-story': 'Vision',
  'deal-attractiveness': 'Deal',
  'social-proof': 'Social proof',
  traction: 'Traction',
  timing: 'Timing',
  'gtm-execution': 'Go-to-market',
  'financial-health': 'Financial health',
  'execution-velocity': 'Execution',
  scalability: 'Scalability',
};

export function SealedCompanyDetail({
  card,
  deal,
}: {
  card: CompanyPublicCard;
  /** The investor's own pipeline record, if any. */
  deal?: { status: DealStage; notes?: string } | null;
}) {
  const colors = useThemeColors();
  const name = card.name ?? 'Company';
  const dims = (card.latest?.dimensions ?? []).filter((d) => d.rawScore !== null);
  const pad = isWeb ? 24 : 20;

  return (
    <AppLayout title={name} breadcrumbs={[{ label: 'Dealflow', href: '/(investor)/dealflow' }, { label: name }]}>
      <ScreenFrame title={name} back={{ fallback: '/(investor)/dealflow', label: 'Dealflow' }}>
        <KeyboardAwareBody contentContainerStyle={{ paddingHorizontal: pad, paddingTop: 8, paddingBottom: 48 }}>
          <View testID="sealed-company-detail" style={{ width: '100%', maxWidth: 720, alignSelf: 'center', gap: 20 }}>
            <ClosedCompanyCard card={card} variant="detail" testID="sealed-detail-card" />

            {dims.length > 0 && (
              <View
                style={{
                  borderRadius: 18,
                  overflow: 'hidden',
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  padding: pad,
                  gap: 10,
                }}
              >
                <Text numberOfLines={1} style={{ color: colors.text.tertiary, fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>
                  LAST SCORE BY DIMENSION
                </Text>
                {dims.map((d, i) => (
                  <View key={`dim-${i}`} className="flex-row items-center" style={{ gap: 12 }}>
                    <Text numberOfLines={1} className="flex-1" style={{ color: colors.text.secondary, fontSize: 14 }}>
                      {DIMENSION_LABEL[d.id] ?? d.id}
                    </Text>
                    <View
                      className="shrink-0"
                      style={{ width: 96, height: 6, borderRadius: 3, backgroundColor: colors.surface.tertiary, overflow: 'hidden' }}
                    >
                      <View
                        style={{
                          width: `${Math.max(0, Math.min(100, d.rawScore ?? 0))}%`,
                          height: 6,
                          backgroundColor: colors.text.tertiary,
                        }}
                      />
                    </View>
                    <Text
                      numberOfLines={1}
                      className="shrink-0"
                      style={{ color: colors.text.primary, fontSize: 14, fontWeight: '700', width: 32, textAlign: 'right', fontVariant: ['tabular-nums'] }}
                    >
                      {Math.round(d.rawScore ?? 0)}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {deal && (
              <View
                testID="sealed-detail-own-record"
                style={{
                  borderRadius: 18,
                  overflow: 'hidden',
                  backgroundColor: colors.surface.secondary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                  padding: pad,
                  gap: 8,
                }}
              >
                <Text numberOfLines={1} style={{ color: colors.text.tertiary, fontSize: 11, fontWeight: '700', letterSpacing: 0.8 }}>
                  YOUR RECORD
                </Text>
                <View className="flex-row items-center" style={{ gap: 8 }}>
                  <Text numberOfLines={1} className="flex-1" style={{ color: colors.text.secondary, fontSize: 14 }}>
                    Stage when it closed
                  </Text>
                  <Text numberOfLines={1} className="shrink-0" style={{ color: colors.text.primary, fontSize: 14, fontWeight: '600' }}>
                    {STAGE_LABEL[deal.status] ?? deal.status}
                  </Text>
                </View>
                {!!deal.notes?.trim() && (
                  <Text numberOfLines={8} style={{ color: colors.text.primary, fontSize: 14, lineHeight: 20 }}>
                    {deal.notes}
                  </Text>
                )}
                <Text numberOfLines={2} style={{ color: colors.text.tertiary, fontSize: 12, lineHeight: 17 }}>
                  Your notes and stage are kept. The company can no longer be moved through your pipeline.
                </Text>
              </View>
            )}
          </View>
        </KeyboardAwareBody>
      </ScreenFrame>
    </AppLayout>
  );
}
